import { useLoader } from '@react-three/fiber';
import { type FC, useContext, useEffect, useMemo } from 'react';
import type { Number3, PointCloudInfo, ViewerObjectProps, WithId } from '../../../types';
import { FileLoaderOptionsContext } from '../../FileLoaderOptionsContext';
import { Octree } from '../Octree';
import { OctreeHierarchyLoader } from '../OctreeHierarchyLoader';
import { OctreeNode } from '../OctreeNode';

export interface OctreePointCloudProps extends ViewerObjectProps {
  url: string;
  requestHeader?: { [header: string]: string };
  material: string;
  nodesToShow?: Set<string>;
  onOctreeHierarchyLoaded?: (info: PointCloudInfo) => void;
  position?: Number3;
  rotation?: Number3;
  scale?: Number3;
  rotationCenter?: Number3;
}

export interface OctreePointCloudExtendedProps extends WithId<OctreePointCloudProps> {
  addOctree: (octree: Octree) => void;
  removeOctree: (octree: Octree) => void;
}

export const OctreePointCloud: FC<OctreePointCloudExtendedProps> = ({
  id,
  isVisible,
  url,
  requestHeader,
  material,
  nodesToShow,
  addOctree,
  removeOctree,
  onOctreeHierarchyLoaded,
}) => {
  const fileLoaderOptions = useContext(FileLoaderOptionsContext);
  const octreeData: Octree = useLoader(OctreeHierarchyLoader, url, (loader) => {
    loader.setWithCredentials(fileLoaderOptions.withCredentials);
    if (requestHeader) {
      loader.setRequestHeader(requestHeader);
    }
  });

  useEffect(() => {
    return () => {
      useLoader.clear(OctreeHierarchyLoader, url);
    };
  }, [url]);

  const octree = useMemo(() => {
    octreeData.id = id;
    octreeData.root.visitAll((node: OctreeNode) => {
      node.octreeId = id;
    });
    return octreeData;
  }, [octreeData, id]);

  useEffect(() => {
    if (onOctreeHierarchyLoaded) {
      const { boundingBox, dataFormat } = octree.root;
      onOctreeHierarchyLoaded({
        url,
        dataFormat,
        boundingBox: {
          min: [boundingBox.min.x, boundingBox.min.y, boundingBox.min.z],
          max: [boundingBox.max.x, boundingBox.max.y, boundingBox.max.z],
        },
      });
    }
  }, [url, octree, onOctreeHierarchyLoaded]);

  useEffect(() => {
    octree.isVisible = isVisible ?? true;
  }, [octree, isVisible]);

  useEffect(() => {
    if (octree != null) {
      octree.material = material;
    }
  }, [octree, material]);

  useEffect(() => {
    if (octree != null) {
      octree.nodesToShow = nodesToShow;
    }
  }, [octree, nodesToShow]);

  useEffect(() => {
    if (octree != null) {
      addOctree(octree);
    }
    return () => {
      if (octree != null) {
        removeOctree(octree);
      }
    };
  }, [octree, addOctree, removeOctree]);

  return <></>;
};
